import React, {useState, useEffect} from "react";
import {compose} from "redux";
import withAPI from "../../services/api";
import FA from "../../containers/fa";
import FAW from "../../containers/faw";
import Checkbox from "@material-ui/core/Checkbox";
import Select from "@material-ui/core/Select";
import MenuItem from "@material-ui/core/MenuItem";
import SettingsInfoPopup from "../UtilComps/SettingsInfoPopup";
import ClosePopupBtn from "../UtilComps/ClosePopupBtn";

// Human-readable labels for every scheme value either provider can use.
const settingsNameMap = {
  '1234': '1234',
  'name': 'Name',
  'email': 'Email',
  'email_before_at': 'Email before @',
  // Clever-specific
  'student_number': 'Student Number',
  'state_id': 'State ID',
  'sis_id': 'SIS ID',
  'clever_id': 'Clever ID',
  'district_username': 'District Username',
  // ClassLink / OneRoster fields
  'identifier': 'Identifier',
  'username': 'Username',
  'sourcedId': 'Sourced ID',
};

const cleverSyncHelpText = (settingChoice) => {
  if (settingChoice === "syncEnabled") {
    return (
      <div>
        <strong>Sync Enabled</strong>
        <p>
          This setting will enable/disable syncing data from the Clever
          system. If disabled, the data will remain but changes to the data
          will not be synced.
        </p>
      </div>
    );
  } else if (settingChoice === "passwordGeneration") {
    return (
      <div>
        <strong>Password Generation options: </strong>
        <ul>
          <li>
            <b>1234: </b>This will set learners' passwords to '1234'.
          </li>
          <li>
            <b>Student Number: </b>This will set learners' passwords to their
            student number, if it's available in Clever. Otherwise, it will
            default to '1234'.
          </li>
          <li>
            <b>State ID: </b>This will set learners' passwords to their state
            ID, if it's available in Clever. Otherwise, it will default to
            '1234'.
          </li>
          <li>
            <b>SIS ID: </b>This will set learners' passwords to their SIS ID.
            This is described by Clever as the "internal student identifier
            from source system" and is guaranteed to be present in the Clever
            data.
          </li>
        </ul>
      </div>
    );
  } else if (settingChoice === "usernameGeneration") {
    return (
      <div>
        <strong>Username Generation options: </strong>
        <ul>
          <li>
            <b>Name: </b>This will generate a username for each learner based
            on their full name. If another learner exists with the same name,
            then it will append numbers on the username to make it unique.
          </li>
          <li>
            <b>Email: </b>This will set every learners' username to their
            email, if it's available in Clever. Otherwise, it will default to
            their SIS ID.
          </li>
          <li>
            <b>Email before @: </b>This will set every learners' username to
            the portion of their email before the @ symbol, if they have an
            email address in Clever. If not, it will default to their SIS ID.
          </li>
          <li>
            <b>District Username: </b>This will set every learners' username
            to their district username, if it's available in Clever.
            Otherwise, it will default to their SIS ID.
          </li>
          <li>
            <b>Student Number: </b>This will set every learners' username to
            their student number, if it's available in Clever. Otherwise, it
            will default to their SIS ID.
          </li>
          <li>
            <b>State ID: </b>This will set every learners' username to their
            state ID, if it's available in Clever. Otherwise, it will default
            to their SIS ID.
          </li>
          <li>
            <b>SIS ID: </b>This will set every learners' username to their
            SIS ID. This is described by Clever as the "internal student
            identifier from source system" and is guaranteed to be present in
            the Clever data.
          </li>
        </ul>
      </div>
    );
  } else if (settingChoice === "orguidGeneration") {
    return (
      <div>
        <strong>Organization UID Generation options: </strong>
        <ul>
          <li>
            <b>Clever ID: </b>This will set every learners' user ID to their
            unique ID in the Clever system.
          </li>
          <li>
            <b>Student Number: </b>This will set every learners' user ID to
            their student number, if it's available in Clever. Otherwise, it
            will default to their Clever ID.
          </li>
          <li>
            <b>State ID: </b>This will set every learners' user ID to
            their state ID, if it's available in Clever. Otherwise, it
            will default to their Clever ID.
          </li>
          <li>
            <b>SIS ID: </b>This will set every learners' user ID to their SIS
            ID. This is described by Clever as the "internal student
            identifier from source system" and is guaranteed to be present in
            the Clever data.
          </li>
        </ul>
      </div>
    );
  }
};

const classlinkSyncHelpText = (settingChoice) => {
  if (settingChoice === "syncEnabled") {
    return (
      <div>
        <strong>Sync Enabled</strong>
        <p>
          This setting will enable/disable syncing roster data from ClassLink
          (via the OneRoster API). If disabled, the data will remain but
          changes to the data will not be synced.
        </p>
      </div>
    );
  } else if (settingChoice === "passwordGeneration") {
    return (
      <div>
        <strong>Password Generation options: </strong>
        <p className='mb-1'>
          Resolved against the learner's OneRoster record. If the chosen field
          is empty, the password falls back to '1234'.
        </p>
        <ul>
          <li>
            <b>1234: </b>Sets every learner's password to '1234'.
          </li>
          <li>
            <b>Identifier: </b>The OneRoster <code>identifier</code> field
            (typically the SIS/local id).
          </li>
          <li>
            <b>Email: </b>The learner's email address.
          </li>
          <li>
            <b>Email before @: </b>The portion of the email before the @ symbol.
          </li>
        </ul>
      </div>
    );
  } else if (settingChoice === "usernameGeneration") {
    return (
      <div>
        <strong>Username Generation options: </strong>
        <p className='mb-1'>
          Resolved against the learner's OneRoster record.
        </p>
        <ul>
          <li>
            <b>Name: </b>Generates a username from the learner's full name,
            appending numbers if needed to keep it unique. (Default when the
            chosen field is unavailable.)
          </li>
          <li>
            <b>Username: </b>The OneRoster <code>username</code> field.
          </li>
          <li>
            <b>Email: </b>The learner's email address.
          </li>
          <li>
            <b>Email before @: </b>The portion of the email before the @ symbol.
          </li>
          <li>
            <b>Identifier: </b>The OneRoster <code>identifier</code> field.
          </li>
        </ul>
      </div>
    );
  } else if (settingChoice === "orguidGeneration") {
    return (
      <div>
        <strong>Organization UID Generation options: </strong>
        <p className='mb-1'>
          Resolved against the learner's OneRoster record. Defaults to the
          Sourced ID if the chosen field is empty.
        </p>
        <ul>
          <li>
            <b>Sourced ID: </b>The OneRoster <code>sourcedId</code> — the
            learner's stable unique id in the roster source.
          </li>
          <li>
            <b>Identifier: </b>The OneRoster <code>identifier</code> field
            (typically the SIS/local id).
          </li>
          <li>
            <b>Username: </b>The OneRoster <code>username</code> field.
          </li>
          <li>
            <b>Email: </b>The learner's email address.
          </li>
        </ul>
      </div>
    );
  }
};

// Per-provider configuration. The Clever entry preserves the modal's original
// behavior exactly; the ClassLink entry uses OneRoster-resolvable fields.
const PROVIDER_CONFIG = {
  clever: {
    label: "Clever",
    keys: {
      enabled: "clever_sync_enabled",
      password: "clever_learner_password_scheme",
      username: "clever_learner_username_scheme",
      orguid: "clever_learner_orguid_scheme",
    },
    defaults: {password: "1234", username: "name", orguid: "student_number"},
    passwordChoices: ["1234", "student_number", "state_id", "sis_id"],
    usernameChoices: ["name", "email", "email_before_at",
      "district_username", "student_number", "state_id", "sis_id"],
    orguidChoices: ["clever_id", "student_number", "state_id", "sis_id"],
    helpText: cleverSyncHelpText,
  },
  classlink: {
    label: "ClassLink",
    keys: {
      enabled: "classlink_sync_enabled",
      password: "classlink_learner_password_scheme",
      username: "classlink_learner_username_scheme",
      orguid: "classlink_learner_orguid_scheme",
    },
    defaults: {password: "1234", username: "name", orguid: "sourcedId"},
    passwordChoices: ["1234", "identifier", "email", "email_before_at"],
    usernameChoices: ["name", "username", "email", "email_before_at", "identifier"],
    orguidChoices: ["sourcedId", "identifier", "username", "email"],
    helpText: classlinkSyncHelpText,
  },
};

const OrgSyncSettingsModal = ({
  api,
  provider = "clever",
  orgId,
  orgSettings,
  refreshOrgInfo,
  setShowModal,
}) => {
  const cfg = PROVIDER_CONFIG[provider] || PROVIDER_CONFIG.clever;

  const [error, setError] = useState(false);
  const [success, setSuccess] = useState(false);
  const [saving, setSaving] = useState(false);

  const [syncEnabled, setSyncEnabled] = useState(false);
  const [passwordGeneration, setPasswordGeneration] = useState(cfg.defaults.password);
  const [usernameGeneration, setUsernameGeneration] = useState(cfg.defaults.username);
  const [orguidGeneration, setOrguidGeneration] = useState(cfg.defaults.orguid);

  useEffect(() => {
    const _settings = orgSettings || {};
    setSyncEnabled(_settings[cfg.keys.enabled] || false);
    setPasswordGeneration(_settings[cfg.keys.password] || cfg.defaults.password);
    setUsernameGeneration(_settings[cfg.keys.username] || cfg.defaults.username);
    setOrguidGeneration(_settings[cfg.keys.orguid] || cfg.defaults.orguid);
  }, [orgSettings, provider]);

  const mapSettingToLabel = (setting) => {
    return settingsNameMap[setting] || setting;
  };

  const [showPopupInfo, setShowPopupInfo] = useState(false);
  const [popupInfo, setPopupInfo] = useState({
    header: "",
    text: "",
  });

  const handleSubmit = () => {
    setError(null);
    setSaving(true);
    setSuccess(false);

    const data = {
      settings: {
        [cfg.keys.enabled]: syncEnabled,
        [cfg.keys.password]: passwordGeneration,
        [cfg.keys.username]: usernameGeneration,
        [cfg.keys.orguid]: orguidGeneration,
      },
    };

    api
      .patchOrganizationInfo(orgId, data)
      .then((resp) => {
        setSaving(false);
        setSuccess(true);
        refreshOrgInfo();
      })
      .catch((err) => {
        setSaving(false);
        setError("Something went wrong... Please try again later.");
        console.log("API error'd", err);
      });
  };
  const handleSyncEnabledChange = (e) => {
    setSyncEnabled(!syncEnabled);
  };

  const handlePasswordGenerationChange = (e) => {
    setPasswordGeneration(e.target.value);
  };

  const handleUsernameGenerationChange = (e) => {
    setUsernameGeneration(e.target.value);
  };

  const handleOrguidGenerationChange = (e) => {
    setOrguidGeneration(e.target.value);
  };

  const getSyncHelpText = (settingChoice) => cfg.helpText(settingChoice);

  const renderSchemeRow = (label, helpKey, value, choices, onChange) => (
    <tr>
      <td className='item-label'>
        {label}&nbsp;
        <span
          onClick={() => {
            setPopupInfo({
              header: `${label} Description`,
              html: getSyncHelpText(helpKey),
            });
            setShowPopupInfo(true);
          }}
        >
          <FAW icon='info-circle' className='info-circle' />
        </span>
      </td>
      <td>
        <Select
          variant='standard'
          displayEmpty={true}
          style={{width: "250px"}}
          value={value || ""}
          onChange={onChange}
        >
          {choices.map((choice, i) => {
            return (
              <MenuItem key={i} value={choice}>
                {value === choice ? (
                  <span>
                    {mapSettingToLabel(choice)}
                    <FAW
                      icon='check'
                      style={{color: "green"}}
                      className='ml-4'
                    />
                  </span>
                ) : (
                  <span>{mapSettingToLabel(choice)}</span>
                )}
              </MenuItem>
            );
          })}
        </Select>
      </td>
    </tr>
  );

  return (
    <div className='pdf-upload'>
      <div className='pdf-upload-wrapper'>
        {showPopupInfo && (
          <SettingsInfoPopup
            popupInfo={popupInfo}
            setShowPopupInfo={setShowPopupInfo}
          />
        )}

        <div className='common_border'>
          {/* Header row */}
          <div className='common_heading'>
            <p>{cfg.label} Sync Settings</p>
            <ClosePopupBtn closePopupFunc={setShowModal} />
          </div>

          {/* Main body */}
          <div className='common_dashboard_bg'>
            <div className='row'>
              <div className='col-12'>
                <div>
                  <table className='table table-sm'>
                    <tbody>

                      <tr>
                        <td className='item-label'>
                          Sync Enabled&nbsp;
                          <span
                            onClick={() => {
                              setPopupInfo({
                                header: "Sync Enabled Description",
                                html: getSyncHelpText("syncEnabled"),
                              });
                              setShowPopupInfo(true);
                            }}
                          >
                            <FAW icon='info-circle' className='info-circle' />
                          </span>
                        </td>
                        <td>
                          <Checkbox
                            style={{color: '#20a8d8'}}
                            checked={syncEnabled}
                            onChange={handleSyncEnabledChange}
                          />
                        </td>
                      </tr>

                      {renderSchemeRow(
                        "Password Generation",
                        "passwordGeneration",
                        passwordGeneration,
                        cfg.passwordChoices,
                        handlePasswordGenerationChange
                      )}

                      {renderSchemeRow(
                        "Username Generation",
                        "usernameGeneration",
                        usernameGeneration,
                        cfg.usernameChoices,
                        handleUsernameGenerationChange
                      )}

                      {renderSchemeRow(
                        "Organization UID Generation",
                        "orguidGeneration",
                        orguidGeneration,
                        cfg.orguidChoices,
                        handleOrguidGenerationChange
                      )}

                      <tr></tr>
                    </tbody>
                  </table>

                  <div className='d-flex'>
                    {!saving && (
                      <button
                        className='btn btn-sm btn-primary mr-2'
                        onClick={() => {
                          handleSubmit();
                        }}
                      >
                        Save Changes
                      </button>
                    )}
                    {saving && (
                      <button className='btn btn-sm btn-primary mr-2'>
                        <FA className='mr-2' icon='spinner' spin />
                        Saving ...
                      </button>
                    )}
                    <button
                      className='btn btn-sm btn-secondary'
                      onClick={() => {
                        setShowModal(false);
                      }}
                    >
                      Close
                    </button>
                  </div>

                  {error && (
                    <div className='alert alert-danger my-2'>{error}</div>
                  )}

                  {success && (
                    <div className='alert alert-success my-2'>
                      {cfg.label} sync settings saved successfully!
                    </div>
                  )}
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default compose(withAPI)(OrgSyncSettingsModal);
